import { ref, type Ref } from 'vue'

import {
  resolveClient,
  type ApolloClientLike,
} from '@/shared/ports/resolveClient'
import {
  GuildNotificationCapabilitiesDocument,
  GuildNotificationSettingsDocument,
  SetGuildNotificationSettingsDocument,
  TestGuildNotificationSettingsDocument,
} from '../lib/handWrittenGraphql'
import type {
  GuildNotificationBotAccountStatusWire,
  GuildNotificationCapabilitiesQuery,
  GuildNotificationCapabilitiesWire,
  GuildNotificationSettingsQuery,
  GuildNotificationSettingsWire,
  SetGuildNotificationSettingsMutation,
  SetGuildNotificationSettingsMutationVariables,
  TestGuildNotificationSettingsMutation,
  TestGuildNotificationSettingsMutationVariables,
} from '../lib/handWrittenGraphql'

export type NotificationMode = 'audio' | 'soundboard'
export type NotificationBotAccountRole = 'coordinator' | 'worker'

export interface GuildNotificationBotAccountStatus {
  readonly index: number
  readonly role: NotificationBotAccountRole
  readonly ready: boolean
  readonly guildPresent: boolean
  readonly inviteUrl: string | null
  readonly requiresOauthCodeGrant: boolean
}

export interface GuildNotificationCapabilities {
  readonly guildId: string
  readonly coordinatorPresent: boolean
  readonly bothBotsPresent: boolean
  readonly configuredBotAccounts: number
  readonly availableBotAccounts: number
  readonly maxConcurrentChannels: number
  readonly teamspeakConfigured: boolean
  readonly botAccountStatuses: ReadonlyArray<GuildNotificationBotAccountStatus>
  readonly supportedModes: ReadonlyArray<NotificationMode>
}

export interface GuildNotificationSettings {
  readonly guildId: string
  readonly enabled: boolean
  readonly automatic: boolean
  readonly intervalSeconds: number
  readonly notificationOffsetsSeconds: ReadonlyArray<number>
  readonly mode: NotificationMode
  readonly voiceChannelIds: ReadonlyArray<string>
  readonly teamspeakChannelIds: ReadonlyArray<string>
  readonly soundboardSoundId: string | null
  readonly soundboardSourceGuildId: string | null
}

export interface GuildNotificationSettingsInput {
  readonly enabled: boolean
  readonly automatic: boolean
  readonly intervalSeconds: number
  readonly notificationOffsetsSeconds: ReadonlyArray<number>
  readonly mode: NotificationMode
  readonly voiceChannelIds: ReadonlyArray<string>
  readonly teamspeakChannelIds: ReadonlyArray<string>
  readonly soundboardSoundId: string | null
  readonly soundboardSourceGuildId: string | null
}

export interface NotificationSettingsApi {
  readonly capabilities: Ref<GuildNotificationCapabilities | null>
  readonly settings: Ref<GuildNotificationSettings | null>
  readonly loading: Ref<boolean>
  readonly error: Ref<string | null>
  readonly load: (guildId: string) => Promise<void>
  readonly save: (
    guildId: string,
    input: GuildNotificationSettingsInput,
  ) => Promise<boolean>
  readonly test: (
    guildId: string,
    input: GuildNotificationSettingsInput,
  ) => Promise<string | null>
  readonly prepareGuild: (guildId: string) => void
  readonly applyLiveState: (
    capabilities: GuildNotificationCapabilitiesWire,
    settings: GuildNotificationSettingsWire,
  ) => void
  readonly reportLiveError: (message: string) => void
}

function notificationMode(value: string): NotificationMode {
  if (value === 'audio' || value === 'soundboard') return value
  throw new Error(`wwm-notify returned unsupported mode "${value}".`)
}

function notificationBotAccountRole(value: string): NotificationBotAccountRole {
  if (value === 'coordinator' || value === 'worker') return value
  throw new Error(`wwm-notify returned unsupported bot account role "${value}".`)
}

function mapBotAccountStatus(
  value: GuildNotificationBotAccountStatusWire,
): GuildNotificationBotAccountStatus {
  return {
    index: value.index,
    role: notificationBotAccountRole(value.role),
    ready: value.ready,
    guildPresent: value.guildPresent,
    inviteUrl: value.inviteUrl,
    requiresOauthCodeGrant: value.requiresOauthCodeGrant,
  }
}

function mapCapabilities(
  value: GuildNotificationCapabilitiesWire,
): GuildNotificationCapabilities {
  const supportedModes = value.supportedModes
    .filter((mode): mode is NotificationMode =>
      mode === 'audio' || mode === 'soundboard'
    )
  return {
    guildId: value.guildId,
    coordinatorPresent: value.coordinatorPresent,
    bothBotsPresent: value.bothBotsPresent,
    configuredBotAccounts: value.configuredBotAccounts,
    availableBotAccounts: value.availableBotAccounts,
    maxConcurrentChannels: value.maxConcurrentChannels,
    teamspeakConfigured: value.teamspeakConfigured ?? false,
    botAccountStatuses: value.botAccountStatuses.map(mapBotAccountStatus),
    supportedModes,
  }
}

function mapSettings(value: GuildNotificationSettingsWire): GuildNotificationSettings {
  return {
    guildId: value.guildId,
    enabled: value.enabled,
    automatic: value.automatic,
    intervalSeconds: value.intervalSeconds,
    notificationOffsetsSeconds: [...value.notificationOffsetsSeconds],
    mode: notificationMode(value.mode),
    voiceChannelIds: [...value.voiceChannelIds],
    teamspeakChannelIds: [...(value.teamspeakChannelIds ?? [])],
    soundboardSoundId: value.soundboardSoundId,
    soundboardSourceGuildId: value.soundboardSourceGuildId,
  }
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause)
}

async function requestCapabilities(
  apolloClient: ApolloClientLike,
  guildId: string,
): Promise<GuildNotificationCapabilities | null> {
  const result = await apolloClient.query<GuildNotificationCapabilitiesQuery>({
    query: GuildNotificationCapabilitiesDocument,
    variables: { guildId } as never,
    fetchPolicy: 'no-cache',
  })
  const capability = result.data?.guildNotificationCapabilities
  return capability === null || capability === undefined
    ? null
    : mapCapabilities(capability)
}

export function useNotificationSettings(
  apolloClient: ApolloClientLike = resolveClient(),
): NotificationSettingsApi {
  const capabilities = ref<GuildNotificationCapabilities | null>(null)
  const settings = ref<GuildNotificationSettings | null>(null)
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)
  let generation = 0
  let liveGeneration = 0

  function prepareGuild(guildId: string): void {
    generation += 1
    liveGeneration += 1
    loading.value = false
    error.value = null
    if (capabilities.value?.guildId !== guildId) capabilities.value = null
    if (settings.value?.guildId !== guildId) settings.value = null
  }

  function applyLiveState(
    nextCapabilities: GuildNotificationCapabilitiesWire,
    nextSettings: GuildNotificationSettingsWire,
  ): void {
    const mappedCapabilities = mapCapabilities(nextCapabilities)
    const mappedSettings = mapSettings(nextSettings)
    liveGeneration += 1
    capabilities.value = mappedCapabilities
    settings.value = mappedSettings
    loading.value = false
    error.value = null
  }

  function reportLiveError(message: string): void {
    loading.value = false
    error.value = message
  }

  async function load(guildId: string): Promise<void> {
    const requestGeneration = ++generation
    const requestLiveGeneration = liveGeneration
    const knownCapabilities = capabilities.value?.guildId === guildId
      ? capabilities.value
      : null
    const keepsCurrentSettings = settings.value?.guildId === guildId
    capabilities.value = knownCapabilities
    if (!keepsCurrentSettings) settings.value = null
    error.value = null
    if (guildId === '0') return
    loading.value = true
    let capabilitiesConfirmed = false
    try {
      const mappedCapabilities = await requestCapabilities(apolloClient, guildId)
      if (requestGeneration !== generation || requestLiveGeneration !== liveGeneration) return
      if (mappedCapabilities === null) {
        capabilities.value = null
        return
      }

      capabilities.value = mappedCapabilities
      capabilitiesConfirmed = true
      const settingsResult = await apolloClient.query<GuildNotificationSettingsQuery>({
        query: GuildNotificationSettingsDocument,
        variables: { guildId } as never,
        fetchPolicy: 'no-cache',
      })
      if (requestGeneration !== generation || requestLiveGeneration !== liveGeneration) return
      const loadedSettings = settingsResult.data?.guildNotificationSettings
      if (loadedSettings === null || loadedSettings === undefined) {
        throw new Error('wwm-notify did not return notification settings.')
      }
      settings.value = mapSettings(loadedSettings)
    } catch (cause: unknown) {
      if (requestGeneration !== generation || requestLiveGeneration !== liveGeneration) return
      if (!capabilitiesConfirmed) capabilities.value = null
      settings.value = null
      error.value = errorMessage(cause)
    } finally {
      if (requestGeneration === generation && requestLiveGeneration === liveGeneration) {
        loading.value = false
      }
    }
  }

  async function save(
    guildId: string,
    input: GuildNotificationSettingsInput,
  ): Promise<boolean> {
    const requestGeneration = ++generation
    const requestLiveGeneration = liveGeneration
    loading.value = true
    error.value = null
    try {
      const currentCapabilities = await requestCapabilities(apolloClient, guildId)
      if (requestGeneration !== generation) return false
      const liveStateArrived = requestLiveGeneration !== liveGeneration
      if (!liveStateArrived) capabilities.value = currentCapabilities
      const capabilitiesAvailable = liveStateArrived
        ? capabilities.value?.guildId === guildId
        : currentCapabilities !== null
      if (!capabilitiesAvailable) return false
      const variables: SetGuildNotificationSettingsMutationVariables = {
        guildId,
        ...input,
      }
      const result = await apolloClient.mutate<SetGuildNotificationSettingsMutation>({
        mutation: SetGuildNotificationSettingsDocument,
        variables: variables as never,
      })
      const stored = result.data?.setGuildNotificationSettings
      if (stored === null || stored === undefined) {
        throw new Error('wwm-notify did not return the saved settings.')
      }
      if (requestGeneration !== generation) return false
      if (requestLiveGeneration === liveGeneration) settings.value = mapSettings(stored)
      return true
    } catch (cause: unknown) {
      if (requestGeneration !== generation) return false
      error.value = errorMessage(cause)
      return false
    } finally {
      if (requestGeneration === generation) loading.value = false
    }
  }

  async function test(
    guildId: string,
    input: GuildNotificationSettingsInput,
  ): Promise<string | null> {
    const requestGeneration = ++generation
    const requestLiveGeneration = liveGeneration
    loading.value = true
    error.value = null
    try {
      const currentCapabilities = await requestCapabilities(apolloClient, guildId)
      if (requestGeneration !== generation) return null
      const liveStateArrived = requestLiveGeneration !== liveGeneration
      if (!liveStateArrived) capabilities.value = currentCapabilities
      const capabilitiesAvailable = liveStateArrived
        ? capabilities.value?.guildId === guildId
        : currentCapabilities !== null
      if (!capabilitiesAvailable) return null
      const variables: TestGuildNotificationSettingsMutationVariables = {
        guildId,
        ...input,
      }
      const result = await apolloClient.mutate<TestGuildNotificationSettingsMutation>({
        mutation: TestGuildNotificationSettingsDocument,
        variables: variables as never,
      })
      const report = result.data?.testGuildNotificationSettings
      if (report === null || report === undefined) {
        throw new Error('wwm-notify did not return the voice-test report.')
      }
      return requestGeneration === generation ? report : null
    } catch (cause: unknown) {
      if (requestGeneration !== generation) return null
      error.value = errorMessage(cause)
      return null
    } finally {
      if (requestGeneration === generation) loading.value = false
    }
  }

  return {
    capabilities,
    settings,
    loading,
    error,
    load,
    save,
    test,
    prepareGuild,
    applyLiveState,
    reportLiveError,
  }
}
